/**
 * Slim useAuth shim — replaces the original Redux-driven hook that
 * owned login / 2FA / logout flows.
 *
 * Auth is now owned by the dashboard. CrmRoot bootstraps a brain
 * crm-api session at /crm entry and dispatches `setAuthData`. This
 * hook exposes a read-only view of that state plus a `logout()` that
 * delegates to the dashboard's logout endpoint and clears the local
 * CRM cache.
 */
import { useCallback } from "react";

import { clearAuth } from "@crm-src/lib/store/authSlice";
import { useAppDispatch, useAppSelector } from "@crm-src/lib/store";

import authService, { type Tenant, type User } from "@crm-src/lib/auth";

export interface UseAuthValue {
  user: User | null;
  tenant: Tenant | null;
  permissions: string[];
  isAuthenticated: boolean;
  isLoading: boolean;
  logout: () => Promise<void>;
}

export function useAuth(): UseAuthValue {
  const dispatch = useAppDispatch();
  const user = useAppSelector((state) => state.auth.user) as User | null;
  const tenant = useAppSelector((state) => state.auth.tenant) as Tenant | null;
  const permissions = useAppSelector((state) => state.auth.permissions) as string[];
  const isLoading = useAppSelector((state) => state.auth.isLoading) as boolean;

  const logout = useCallback(async () => {
    try {
      await fetch("/api/auth/logout", { method: "DELETE" });
    } catch {
      // Best-effort. Dashboard server logout is the source of truth;
      // a network error shouldn't block the local clear.
    }
    authService.clearAuth();
    dispatch(clearAuth());
    if (typeof window !== "undefined") {
      window.location.assign("/login");
    }
  }, [dispatch]);

  return {
    user,
    tenant,
    permissions,
    isAuthenticated: Boolean(user),
    isLoading,
    logout,
  };
}

export default useAuth;
